import { useCallback, useState } from "react";

import { addScraperChat, resolveChat } from "../api/endpoints";
import { describeError } from "../api/errors";
import type { ScraperChat } from "../api/types";

interface ScraperSearchState {
  resolved: ScraperChat | null;
  added: ScraperChat | null;
  working: boolean;
  error: string | null;
}

export interface UseScraperSearch extends ScraperSearchState {
  resolve: (row: string) => void;
  add: (row: string) => void;
  reset: () => void;
}

export function useScraperSearch(): UseScraperSearch {
  const [state, setState] = useState<ScraperSearchState>({
    resolved: null,
    added: null,
    working: false,
    error: null,
  });

  const resolve = useCallback(async (row: string) => {
    setState({ resolved: null, added: null, working: true, error: null });
    try {
      const chat = await resolveChat(row);
      setState({ resolved: chat, added: null, working: false, error: null });
    } catch (error) {
      setState({ resolved: null, added: null, working: false, error: describeError(error) });
    }
  }, []);

  /**
   * Find which scraper is already in the chat and save it.
   *
   * Minutes, not seconds: the search walks the dialogs of every scraper account. Nothing is
   * cancelled here on purpose - an aborted request would leave the search running on the api
   * anyway, and the admin would not learn whether it worked.
   */
  const add = useCallback(async (row: string) => {
    setState((previous) => ({ ...previous, added: null, working: true, error: null }));
    try {
      const chat = await addScraperChat(row);
      setState((previous) => ({ ...previous, added: chat, working: false, error: null }));
    } catch (error) {
      setState((previous) => ({ ...previous, working: false, error: describeError(error) }));
    }
  }, []);

  const reset = useCallback(() => {
    setState({ resolved: null, added: null, working: false, error: null });
  }, []);

  return {
    ...state,
    resolve: (row: string) => void resolve(row),
    add: (row: string) => void add(row),
    reset,
  };
}
